import React, { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, AlertCircle, AlertTriangle, Info, XCircle } from 'lucide-react';
import { OrderTimelineLogEntryDto } from '../../../../../types/orderTimeline';

interface TimelineLogEntriesProps {
  logEntries: OrderTimelineLogEntryDto[];
}

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const getLevelStyle = (level?: string) => {
  switch (level?.toLowerCase()) {
    case 'information': return { icon: Info, bg: 'bg-blue-50', text: 'text-blue-600', label: 'Info' };
    case 'warning': return { icon: AlertTriangle, bg: 'bg-yellow-50', text: 'text-yellow-600', label: 'Warning' };
    case 'error': return { icon: AlertCircle, bg: 'bg-red-50', text: 'text-red-600', label: 'Error' };
    case 'fatal': return { icon: XCircle, bg: 'bg-red-100', text: 'text-red-800', label: 'Fatal' };
    default: return { icon: FileText, bg: 'bg-gray-50', text: 'text-gray-600', label: level || '?' };
  }
};

export const TimelineLogEntries: React.FC<TimelineLogEntriesProps> = ({ logEntries }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <FileText className="w-4 h-4" />
        Applikasjonslogger ({logEntries.length})
      </button>

      {expanded && (
        <div className="space-y-1.5">
          {logEntries.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-4 bg-white rounded-lg border border-gray-200">
              Ingen applikasjonslogger funnet for denne ordren
            </div>
          ) : (
            logEntries.map((entry) => {
              const style = getLevelStyle(entry.level);
              const Icon = style.icon;
              return (
                <div key={entry.id} className={`${style.bg} border border-gray-200 rounded-lg p-2.5`}>
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.text}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800 break-words">
                        {entry.message || '–'}
                      </div>
                      {entry.exception && (
                        <div className="text-xs text-red-600 mt-1 bg-red-50 p-1.5 rounded font-mono whitespace-pre-wrap max-h-24 overflow-y-auto">
                          {entry.exception}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400 flex-wrap">
                        <span>{formatDateTime(entry.timeStamp)}</span>
                        {entry.operatorId && (
                          <span>Operatør: {entry.operatorName || entry.operatorId}</span>
                        )}
                        {entry.requestPath && (
                          <span className="font-mono">
                            {entry.requestMethod} {entry.requestPath}
                          </span>
                        )}
                        <span className={`px-1 py-0 rounded ${style.text} bg-white/50`}>{style.label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
